import React, { useState, useEffect } from "react";

// Import Esri Components
import Graphic from "@arcgis/core/Graphic";
import * as query from "@arcgis/core/rest/query.js";
import Query from "@arcgis/core/rest/support/Query.js";

import axios from "axios";

// Functions
// import { buildResizeContainer } from "../Resize";
import { useDebounce } from "../useDebounce";

// Import Semantic UI React
import {
  Menu,
  MenuItem,
  Icon,
  List,
  ListItem,
  ListHeader,
  ListContent,
  ListIcon,
  Input,
  Segment,
  Button,
  Popup,
} from "semantic-ui-react";

interface Props {
  service: string;
  city_scope?: string | null;
  result_callback: (arg1: SearchResult) => void;
}

interface SearchResult {
  value: string;
  field: string;
}

interface SearchObj {
  address: SearchResult[];
  owner: SearchResult[];
  parcel: SearchResult[];
}

const compare = (a, b) => {
  if (a.value < b.value) {
    return -1;
  }
  if (a.value > b.value) {
    return 1;
  }
  return 0;
};

const SearchWidget = ({
  service,
  city_scope = null,
  result_callback,
}: Props) => {
  const [debounceValue, setDebounceValue] = useState("");
  const [term, setTerm] = useState("");
  const [selected, setSelected] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [selectedTab, setSelectedTab] = useState(null);
  const [addressResults, setAddressResults] = useState([]);
  const [ownerResults, setOwnerResults] = useState([]);
  const [parcelResults, setparcelResults] = useState([]);
  const [greeting, setGreeting] = useState(true);

  const debounce = useDebounce(term, 500);

  useEffect(() => {
    if (debounceValue.length >= 3 && !submitted) {
      fetchTerm(debounceValue);
    }
  }, [debounceValue]);

  useEffect(() => {
    setDebounceValue(term);
  }, [debounce]);

  useEffect(() => {
    if (selected) {
      queryParcels(selected);
    }
  }, [selected]);

  useEffect(() => {
    if (term.length === 0 && hasResults()) {
      reset();
    }
  }, [term]);

  const reset = () => {
    setAddressResults([]);
    setOwnerResults([]);
    setparcelResults([]);
    setSubmitted(false);
    setSelectedTab(null);
  };

  const queryParcels = (record: SearchResult) => {
    result_callback(record);
    setTimeout(() => {
      setSelected(null);
    }, 1500);
  };

  const fetchTerm = (value: string) => {
    setSubmitted(true);
    setGreeting(false);
    var upper_term = value.toUpperCase();

    let scope = "";
    if (city_scope) {
      scope = `AND UPPER(SitusCityN)='${city_scope.toUpperCase()}'`;
    }

    const queryAddress = new Query();
    queryAddress.returnGeometry = false;
    queryAddress.outFields = ["FullSitus", "SitusCityN"];
    queryAddress.where = `UPPER(FullSitus) LIKE '%${upper_term}%' ${scope}`;
    queryAddress.orderByFields = ["FullSitus DESC", "SitusCityN DESC"];

    const queryOwner = new Query();
    queryOwner.returnGeometry = false;
    queryOwner.outFields = ["OwnerName"];
    queryOwner.where = `UPPER(OwnerName) LIKE '%${upper_term}%' ${scope}`;
    queryOwner.orderByFields = ["OwnerName DESC"];

    const queryParcelNO = new Query();
    queryParcelNO.returnGeometry = false;
    queryParcelNO.outFields = ["PARCEL_NO"];
    queryParcelNO.where = `UPPER(PARCEL_NO) LIKE '%${upper_term}%' ${scope}`;
    queryParcelNO.orderByFields = ["PARCEL_NO DESC"];

    const queryParcelNumb = new Query();
    queryParcelNumb.returnGeometry = false;
    queryParcelNumb.outFields = ["ParcelNumb"];
    queryParcelNumb.where = `UPPER(ParcelNumb) LIKE '%${upper_term}%' ${scope}`;
    queryParcelNumb.orderByFields = ["ParcelNumb DESC"];

    Promise.all([
      query.executeQueryJSON(service, queryAddress),
      query.executeQueryJSON(service, queryOwner),
      query.executeQueryJSON(service, queryParcelNO),
      query.executeQueryJSON(service, queryParcelNumb),
    ]).then((result) => {
      var selected_list = null;

      const data = {
        address: result[0].features
          .filter(
            (value, index, self) =>
              index ===
              self.findIndex(
                (record) =>
                  record.attributes.FullSitus === value.attributes.FullSitus
              )
          )
          .map((record) => {
            return {
              value: record.attributes.FullSitus,
              field: "FullSitus",
              label: `${record.attributes.FullSitus}, ${record.attributes.SitusCityN}`,
            };
          })
          .sort(compare),
        owner: result[1].features
          .filter(
            (value, index, self) =>
              index ===
              self.findIndex(
                (record) =>
                  record.attributes.OwnerName === value.attributes.OwnerName
              )
          )
          .map((record) => {
            return {
              value: record.attributes.OwnerName,
              field: "OwnerName",
              label: record.attributes.OwnerName,
            };
          })
          .sort(compare),
        parcel: [],
      };

      // concat the parcels results
      var parcels = result[2].features
        .map((record) => {
          return {
            value: record.attributes.PARCEL_NO,
            field: "PARCEL_NO",
            label: record.attributes.PARCEL_NO,
          };
        })
        .concat(
          result[3].features.map((record) => {
            return {
              value: record.attributes.ParcelNumb,
              field: "ParcelNumb",
              label: record.attributes.ParcelNumb,
            };
          })
        );

      // add teh parcels to the data object and sort them
      data.parcel = parcels.sort(compare);

      selected_list = null;

      // Determine which list to be selected
      // => default order is address, then owner, then parcels
      if (data.address.length > 0) {
        selected_list = "address";
      } else if (data.owner.length > 0) {
        selected_list = "owner";
      } else if (data.parcel.length > 0) {
        selected_list = "parcel";
      }

      setparcelResults(data.parcel);
      setAddressResults(data.address);
      setOwnerResults(data.owner);
      setSelectedTab(selected_list);
      setSubmitted(false);
    });
  };

  // const submitHandler = () => {
  //   if (term.length >= 3) {
  //     fetchTerm();
  //   }
  // };

  const hasResults = () => {
    return (
      addressResults.length > 0 ||
      ownerResults.length > 0 ||
      parcelResults.length > 0
    );
  };

  const resultsCount = () => {
    let count = 0;

    if (addressResults.length > 0) count += 1;
    if (ownerResults.length > 0) count += 1;
    if (parcelResults.length > 0) count += 1;

    return count > 0 ? count : null;
  };

  const renderList = () => {
    if (!hasResults()) {
      return null;
    }

    let records = [];

    if (selectedTab === "address") {
      records = addressResults;
    } else if (selectedTab === "owner") {
      records = ownerResults;
    } else if (selectedTab === "parcel") {
      records = parcelResults;
    }

    return (
      <div
        className="no-margin"
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "auto",
        }}
      >
        <Segment style={{ paddijng: 0 }}>
          <List divided relaxed>
            {records.map((record, index) => {
              return (
                <ListItem
                  as="a"
                  onClick={() => setSelected(record)}
                  key={index}
                  style={{ padding: "1em 0.5em" }}
                >
                  <ListContent>
                    <ListHeader>
                      {selected && selected.value === record.value && (
                        <Icon
                          loading
                          name="circle notch"
                          style={{ color: "black" }}
                        />
                      )}
                      {record.label}
                    </ListHeader>
                  </ListContent>
                </ListItem>
              );
            })}
          </List>
        </Segment>
      </div>
    );
  };

  // console.log("SearchWidget", {
  //   term,
  //   service,
  //   selectedTab,
  //   addressResults,
  //   ownerResults,
  //   parcelResults,
  //   hasResults: hasResults(),
  //   resultsCount: resultsCount(),
  // });

  const renderIcon = () => {
    if (submitted) {
      return (
        <Icon
          link
          loading
          id="clear_search_btn"
          className="middle aligned no-padding icon"
          name={"circle notch"}
          style={{ right: "5px", cursor: "pointer" }}
        />
      );
    }

    if (!submitted && term.length > 1) {
      return (
        <Icon
          link
          id="clear_search_btn"
          className="middle aligned no-padding icon"
          name={"delete"}
          style={{ right: "5px", cursor: "pointer" }}
          onClick={() => {
            setTerm("");
            setGreeting(true);
          }}
        />
      );
    }
  };

  return (
    <div
      style={{
        minHeight: "calc(100vh - 82px)",
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        overflow: "hidden",
      }}
    >
      <Input
        fluid
        style={{ margin: "0.5em 0.25em" }}
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        icon={renderIcon()}
      />

      {submitted && (
        <Segment style={{ margin: 0, textAlign: "center" }}>
          Searching...
        </Segment>
      )}

      {!hasResults() && !submitted && greeting && (
        <Segment style={{ margin: 0, textAlign: "center" }}>
          Search by Owner, Address, or Parcel Number
        </Segment>
      )}

      {!hasResults() && !submitted && !greeting && (
        <Segment style={{ margin: 0, textAlign: "center" }}>
          No Results Found
        </Segment>
      )}

      {hasResults() && !submitted && (
        // @ts-ignore
        <Menu fluid widths={resultsCount()} style={{ margin: 0 }}>
          {addressResults.length > 0 && (
            <MenuItem
              name="address"
              active={selectedTab === "address"}
              onClick={() => setSelectedTab("address")}
            />
          )}
          {ownerResults.length > 0 && (
            <MenuItem
              name="owner"
              active={selectedTab === "owner"}
              onClick={() => setSelectedTab("owner")}
            />
          )}
          {parcelResults.length > 0 && (
            <MenuItem
              name="parcel"
              active={selectedTab === "parcel"}
              onClick={() => setSelectedTab("parcel")}
            />
          )}
        </Menu>
      )}

      {!submitted && renderList()}
    </div>
  );
};

export default SearchWidget;
